import type { Grid, GridPosition, Payline, ReelStops, ReelSymbolId, RegularSymbolId } from '../core/types';
import { PAYLINES } from '../core/paylines';
import { decodeReplayDraftV2 } from '../replay/decoder';
import type { PresentedLineWin, SpinPresentationResult } from '../rendering/types';
import type { ExtremeBaseStepV1, ExtremeFeatureDraftV1, ExtremeRunStateV1 } from './types';
import { EXTREME_BET_CENTS, EXTREME_PAID_SPINS, EXTREME_START_BALANCE_CENTS } from './types';

type UnknownRecord = Record<string, unknown>;
const SYMBOLS = new Set<ReelSymbolId>(['BEWD', 'DM', 'JINZO', 'TW', 'ACE', 'KING', 'QUEEN', 'JACK', 'WILD', 'SCATTER']);
const PAYING = new Set<RegularSymbolId | 'WILD'>(['BEWD', 'DM', 'JINZO', 'TW', 'ACE', 'KING', 'QUEEN', 'JACK', 'WILD']);

function record(value: unknown): UnknownRecord | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? value as UnknownRecord : null;
}

function integer(value: unknown, min = 0, max = Number.MAX_SAFE_INTEGER): number | null {
  return Number.isSafeInteger(value) && Number(value) >= min && Number(value) <= max ? Number(value) : null;
}

function id(value: unknown): string | null {
  return typeof value === 'string' && /^[A-Za-z0-9_-]{1,128}$/.test(value) ? value : null;
}

function playerName(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const result = value.replace(/\s+/g, ' ').trim().slice(0, 20);
  return result || null;
}

function decodeGrid(value: unknown): Grid | null {
  if (!Array.isArray(value) || value.length !== 5) return null;
  const columns: ReelSymbolId[][] = [];
  for (const column of value) {
    if (!Array.isArray(column) || column.length !== 3 || column.some((symbol) => !SYMBOLS.has(symbol as ReelSymbolId))) return null;
    columns.push(column as ReelSymbolId[]);
  }
  return columns as unknown as Grid;
}

function decodeStops(value: unknown): ReelStops | null {
  if (!Array.isArray(value) || value.length !== 5) return null;
  const result = value.map((stop) => integer(stop, 0, 57));
  return result.some((stop) => stop === null) ? null : result as unknown as ReelStops;
}

function decodePosition(value: unknown): GridPosition | null {
  const source = record(value);
  const reel = integer(source?.reel, 0, 4);
  const row = integer(source?.row, 0, 2);
  return reel === null || row === null ? null : { reel, row } as GridPosition;
}

function decodePositions(value: unknown): readonly GridPosition[] | null {
  if (!Array.isArray(value) || value.length > 15) return null;
  const result = value.map(decodePosition);
  if (result.some((position) => position === null)) return null;
  return result as readonly GridPosition[];
}

function decodeLineWins(value: unknown): readonly PresentedLineWin[] | null {
  if (!Array.isArray(value) || value.length > 10) return null;
  const result: PresentedLineWin[] = [];
  for (const raw of value) {
    const source = record(raw);
    const lineIndex = integer(source?.lineIndex, 0, 9);
    const count = integer(source?.count, 3, 5);
    const winCents = integer(source?.winCents, 0);
    const symbolId = source?.symbolId as RegularSymbolId | 'WILD';
    const route = source?.route;
    if (source === null || lineIndex === null || count === null || winCents === null || !PAYING.has(symbolId)
      || !Array.isArray(route) || JSON.stringify(route) !== JSON.stringify(PAYLINES[lineIndex])) return null;
    result.push({ lineIndex, route: route as unknown as Payline, count: count as 3 | 4 | 5, symbolId, winCents });
  }
  return result;
}

function decodePresentation(value: unknown): SpinPresentationResult | null {
  const source = record(value);
  const grid = decodeGrid(source?.grid);
  const stops = decodeStops(source?.stops);
  const scatterCount = integer(source?.scatterCount, 0, 5);
  const totalWinCents = integer(source?.totalWinCents, 0);
  const lineWins = decodeLineWins(source?.lineWins);
  const stickyBefore = decodePositions(source?.stickyBefore);
  const stickyAfter = decodePositions(source?.stickyAfter);
  const newlySticky = decodePositions(source?.newlySticky);
  if (source === null || grid === null || stops === null || scatterCount === null || totalWinCents === null
    || lineWins === null || stickyBefore === null || stickyAfter === null || newlySticky === null
    || stickyBefore.length > 0 || stickyAfter.length > 0 || newlySticky.length > 0
    || grid.flat().filter((symbol) => symbol === 'SCATTER').length !== scatterCount
    || lineWins.reduce((sum, win) => sum + win.winCents, 0) !== totalWinCents) return null;
  return { grid, stops, scatterCount, totalWinCents, lineWins, stickyBefore, stickyAfter, newlySticky };
}

function decodeBaseSteps(value: unknown, paidSpins: number): readonly ExtremeBaseStepV1[] | null {
  if (!Array.isArray(value) || value.length !== paidSpins || value.length > EXTREME_PAID_SPINS) return null;
  const result: ExtremeBaseStepV1[] = [];
  for (const [index, raw] of value.entries()) {
    const source = record(raw);
    const balanceAfterCents = integer(source?.balanceAfterCents, 0);
    const presentation = decodePresentation(source?.presentation);
    if (source === null || source.kind !== 'base' || source.paidSpin !== index + 1 || balanceAfterCents === null
      || typeof source.featureTriggered !== 'boolean' || presentation === null) return null;
    result.push({ kind: 'base', paidSpin: index + 1, balanceAfterCents, featureTriggered: source.featureTriggered, presentation });
  }
  return result;
}

function decodeFeatures(value: unknown, featureCount: number, paidSpins: number): readonly ExtremeFeatureDraftV1[] | null {
  if (!Array.isArray(value) || value.length > featureCount) return null;
  const result: ExtremeFeatureDraftV1[] = [];
  for (const [index, raw] of value.entries()) {
    const source = record(raw);
    const triggerPaidSpin = integer(source?.triggerPaidSpin, 1, paidSpins);
    const replayDraft = decodeReplayDraftV2(source?.replayDraft);
    if (source === null || source.featureIndex !== index + 1 || triggerPaidSpin === null || replayDraft === null
      || replayDraft.steps.length === 0) return null;
    result.push({ featureIndex: index + 1, triggerPaidSpin, replayDraft });
  }
  return result;
}

export function decodeExtremeRunState(value: unknown): ExtremeRunStateV1 | null {
  if (value === null) return null;
  const source = record(value);
  const runId = id(source?.id);
  const name = playerName(source?.name);
  const returnBalanceCents = integer(source?.returnBalanceCents, 0);
  const returnBetCents = integer(source?.returnBetCents, 1, 2_000);
  const startedAt = integer(source?.startedAt, 1);
  const paidSpinsCompleted = integer(source?.paidSpinsCompleted, 0, EXTREME_PAID_SPINS);
  const featureCount = integer(source?.featureCount, 0, EXTREME_PAID_SPINS);
  if (source === null || source.version !== 1 || source.active !== true || !runId || !name
    || source.betCents !== EXTREME_BET_CENTS || source.startBalanceCents !== EXTREME_START_BALANCE_CENTS
    || returnBalanceCents === null || returnBetCents === null || startedAt === null
    || paidSpinsCompleted === null || featureCount === null) return null;
  const baseSteps = decodeBaseSteps(source.baseSteps, paidSpinsCompleted);
  const completedFeatures = decodeFeatures(source.completedFeatures, featureCount, paidSpinsCompleted);
  if (baseSteps === null || completedFeatures === null
    || baseSteps.filter((step) => step.featureTriggered).length !== featureCount) return null;
  return {
    version: 1, id: runId, active: true, name, betCents: EXTREME_BET_CENTS,
    startBalanceCents: EXTREME_START_BALANCE_CENTS, returnBalanceCents, returnBetCents,
    startedAt, paidSpinsCompleted, featureCount, baseSteps, completedFeatures,
  };
}
